import { useParams, useNavigate } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { ArrowLeft, ExternalLink, Users, Globe2, Lock } from "lucide-react";
import { useFetch } from "@/lib/hooks";
import { useAuth } from "@/lib/auth";
import { PageHeader, StatusBadge, Timeline, LegalPanel, LiveIndicator, Loading, CopyText, DemoBadge, Row, Empty } from "@/components/common";
import { GeoMap } from "@/components/GeoMap";
import { SocialBar, CommentsPanel, DueBadge } from "@/components/community";
import { fdate } from "@/lib/api";

const LABELS = { recibido: "Recibido", validado: "Validado", asignado: "Asignado a cuadrilla", en_proceso: "En proceso", resuelto: "Resuelto", cerrado: "Cerrado", rechazado: "Rechazado" };

export default function ReportDetail() {
  const { folio } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { data: r, error, lastSync, setData, reload } = useFetch(`/reports/${folio}`, { poll: 5000 });
  if (error) return <Empty title="No pudimos abrir este folio" hint={error?.response?.data?.detail} action={<Button variant="outline" onClick={() => navigate(-1)}>Regresar</Button>} />;
  if (!r) return <Loading rows={4} />;
  const isPublic = r.is_public && !r.hidden_public;
  const staff = user.role !== "ciudadano";
  return (
    <div>
      <Button variant="ghost" className="mb-2 -ml-2" onClick={() => navigate(-1)} data-testid="report-detail-back"><ArrowLeft className="h-4 w-4 mr-1" />Regresar</Button>
      <PageHeader eyebrow={r.dependency_label} title={r.category_label}
        subtitle={<span className="flex flex-wrap items-center gap-2"><CopyText value={r.folio} className="text-sm" testid="report-folio-copy-button" /><StatusBadge status={r.status} testid="report-detail-status" />
          {isPublic ? <Badge variant="outline" className="bg-[#DFF3F6] text-[#006A7C] border-[#b9e2e8]" data-testid="report-public-badge"><Globe2 className="h-3 w-3 mr-1" />Público</Badge>
            : <Badge variant="outline" data-testid="report-private-badge"><Lock className="h-3 w-3 mr-1" />{r.is_public ? "Oculto por la dependencia" : "Privado"}</Badge>}
          {r.is_demo && <DemoBadge />}</span>}
        actions={<><LiveIndicator lastSync={lastSync} /><Button variant="outline" onClick={() => window.open(`/verificar/${r.folio}`, "_blank")} data-testid="report-verify-public-button"><ExternalLink className="h-4 w-4 mr-1" />Verificación pública</Button></>} />
      {isPublic && (
        <Card className="mb-4"><CardContent className="p-4 flex flex-wrap items-center gap-3">
          <SocialBar folio={r.folio} state={r} onChange={(s) => setData((d) => ({ ...d, ...s }))} />
          <span className="text-xs text-muted-foreground">{r.subscriber_count} vecinos siguen este reporte · {r.comment_count || 0} comentarios{r.author ? ` · Reportó: ${r.author}` : ""}</span>
        </CardContent></Card>
      )}
      <div className="grid gap-4 lg:grid-cols-[1.4fr_1fr]">
        <Card>
          <CardContent className="p-5">
            <Tabs defaultValue={r.mine || staff ? "actividad" : "comentarios"}>
              <TabsList className="flex-wrap h-auto">
                <TabsTrigger value="actividad" data-testid="report-tab-actividad">Actividad</TabsTrigger>
                <TabsTrigger value="evidencia" data-testid="report-tab-evidencia">Evidencia</TabsTrigger>
                {(isPublic || staff) && <TabsTrigger value="comentarios" data-testid="report-tab-comentarios">Vecinos ({r.comment_count || 0})</TabsTrigger>}
                <TabsTrigger value="legal" data-testid="report-tab-legal">Trazabilidad</TabsTrigger>
              </TabsList>
              <TabsContent value="actividad" className="pt-4"><Timeline items={r.timeline} labels={LABELS} /></TabsContent>
              <TabsContent value="evidencia" className="pt-4 space-y-3">
                <p className="text-sm">{r.description}</p>
                {r.extra_fields?.length > 0 && <div className="space-y-1.5">{r.extra_fields.map((x) => <Row key={x.key} label={x.label}><span className="text-sm">{x.type === "checkbox" ? (x.value ? "Sí" : "No") : String(x.value)}</span></Row>)}</div>}
                {r.photo ? <img src={r.photo} alt="evidencia" className="rounded-lg border max-h-80 object-cover" data-testid="report-detail-photo" /> : <div className="text-sm text-muted-foreground">Sin foto adjunta.</div>}
              </TabsContent>
              {(isPublic || staff) && <TabsContent value="comentarios" className="pt-4"><CommentsPanel folio={r.folio} staff={staff} onCount={reload} /></TabsContent>}
              <TabsContent value="legal" className="pt-4"><LegalPanel data_hash={r.data_hash} nom151={r.nom151} anchor={r.anchor} /></TabsContent>
            </Tabs>
          </CardContent>
        </Card>
        <div className="space-y-4">
          <Card>
            <CardHeader className="pb-2"><CardTitle className="text-base">Ubicación</CardTitle></CardHeader>
            <CardContent>
              <GeoMap center={[r.lat, r.lng]} zoom={15} pitch={45} className="h-[240px]" testid="report-detail-map" picked={[r.lat, r.lng]} compact showUser={false} />
              <div className="mt-3 space-y-2 text-sm">
                <Row label="Municipio">{r.municipio}</Row>
                {r.colonia && <Row label="Colonia">{r.colonia}</Row>}
                {r.address && <Row label="Referencia">{r.address}</Row>}
                <Row label="Coordenadas"><span className="font-mono text-xs">{r.lat.toFixed(5)}, {r.lng.toFixed(5)}{r.accuracy_m ? ` · ±${r.accuracy_m} m` : ""}</span></Row>
                <Row label="Registrado">{fdate(r.created_at)}</Row>
                <Row label="Prioridad"><StatusBadge status={r.priority} /></Row>
                {r.assignee && <Row label="Responsable">{r.assignee}</Row>}
                {r.due_at && <Row label="Plazo de atención"><DueBadge due={r.due_at} status={r.status} testid="report-detail-due" /></Row>}
              </div>
            </CardContent>
          </Card>
          {r.crew && (
            <Card><CardContent className="p-4 flex items-center gap-3">
              <span className="rounded-lg bg-[#DFF3F6] p-2 text-[#006A7C]"><Users className="h-5 w-5" /></span>
              <div><div className="text-sm font-medium">{r.crew.name}</div><div className="text-xs text-muted-foreground">{(r.crew.members || []).join(", ")}</div></div>
            </CardContent></Card>
          )}
        </div>
      </div>
    </div>
  );
}
